import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronRight, BookOpen, Target, Lightbulb, Brain, Loader2 } from 'lucide-react';
import { getGradeData } from '../data/primaryChineseData';
import { showNotification } from './Notification';
import AIDrillModal from './AIDrillModal';
import DrillQuestions from './DrillQuestions';

const PrimaryGradeContent = ({ grade }) => {
  const [expandedTopics, setExpandedTopics] = useState(new Set());
  const [drillData, setDrillData] = useState({});
  const [isDrillModalOpen, setIsDrillModalOpen] = useState(false);
  const [currentDrillQuestion, setCurrentDrillQuestion] = useState(null);
  const [gradeData, setGradeData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load grade data when grade changes
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        showNotification(`正在載入 ${grade.toUpperCase()} 學習內容...`, 'info', 2000);
        
        const data = await getGradeData(grade);
        
        if (!data) {
          setError(`未找到 ${grade.toUpperCase()} 的學習內容`);
          setGradeData(null);
          showNotification(`未找到 ${grade.toUpperCase()} 的學習內容`, 'warning', 3000);
        } else {
          setGradeData(data);
          showNotification(`已成功載入 ${grade.toUpperCase()} 學習內容`, 'success', 3000);
        }
      } catch (err) {
        console.error('Error loading grade data:', err);
        setError(`載入失敗：${err.message}`);
        setGradeData(null);
        showNotification(`載入失敗：${err.message}`, 'error', 5000);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [grade]);
  
  // Show loading state
  if (isLoading) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">正在載入學習內容...</h3>
        <p className="text-gray-600">正在載入資料中...</p>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <BookOpen className="w-8 h-8 text-red-500" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">載入錯誤</h3>
        <p className="text-red-600 mb-4">{error}</p>
        <button
          onClick={() => {
            setIsLoading(true);
            setError(null);
            const loadData = async () => {
              try {
                const data = await getGradeData(grade);
                setGradeData(data);
                setError(null);
              } catch (err) {
                setError(`載入失敗：${err.message}`);
              } finally {
                setIsLoading(false);
              }
            };
            loadData();
          }}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          重新載入
        </button>
      </div>
    );
  }

  // Show no data state
  if (!gradeData) {
    return (
      <div className="text-center py-8">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <BookOpen className="w-8 h-8 text-gray-400" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">未找到內容</h3>
        <p className="text-gray-600">該年級的學習內容尚未準備</p>
      </div>
    );
  }

  const toggleTopic = (topicId) => {
    const newExpanded = new Set(expandedTopics);
    if (newExpanded.has(topicId)) {
      newExpanded.delete(topicId);
    } else {
      newExpanded.add(topicId);
    }
    setExpandedTopics(newExpanded);
  };

  const handleAIDrill = (question, topicTitle, questionIndex) => {
    setCurrentDrillQuestion({
      question: question.question,
      topicTitle,
      questionIndex
    });
    setIsDrillModalOpen(true);
  };

  const handleDrillGenerated = (result) => {
    const key = `${currentDrillQuestion.topicTitle}-${currentDrillQuestion.questionIndex}`;
    setDrillData(prev => ({
      ...prev,
      [key]: result
    }));
  };

  const handleNewDrill = () => {
    if (currentDrillQuestion) {
      handleAIDrill(
        { question: currentDrillQuestion.question },
        currentDrillQuestion.topicTitle,
        currentDrillQuestion.questionIndex
      );
    }
  };

  return (
    <div className="space-y-6">
      {/* Grade Header */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg flex items-center justify-center">
            <BookOpen className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{gradeData.title}</h2>
            <p className="text-gray-600">{gradeData.description}</p>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white p-4 rounded-lg border border-blue-200">
            <h3 className="font-semibold text-blue-900 mb-2">學習目標</h3>
            <p className="text-sm text-blue-700">{gradeData.objectives}</p>
          </div>
          <div className="bg-white p-4 rounded-lg border border-green-200">
            <h3 className="font-semibold text-green-900 mb-2">學習重點</h3>
            <p className="text-sm text-green-700">{gradeData.focus}</p>
          </div>
        </div>
      </div>

      {/* Reading Comprehension Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="border-b border-gray-200 p-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
            <BookOpen className="w-5 h-5 text-blue-600" />
            <span>閱讀理解篇章類型</span>
          </h3>
        </div>
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {(gradeData.readingTypes || []).map((type, index) => (
              <div key={index} className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                <span className="text-sm font-medium text-blue-900">{type}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Language Usage Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="border-b border-gray-200 p-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
            <Target className="w-5 h-5 text-green-600" />
            <span>語文運用</span>
          </h3>
        </div>
        <div className="p-4">
          <div className="space-y-4">
            {(gradeData.languageUsage || []).map((topic, index) => (
              <div key={index} className="border border-gray-200 rounded-lg">
                <button
                  onClick={() => toggleTopic(`topic-${index}`)}
                  className="w-full p-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-blue-500 rounded-full flex items-center justify-center text-white text-sm font-bold">
                      {index + 1}
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">{topic.title}</h4>
                      <p className="text-sm text-gray-600">{topic.description}</p>
                    </div>
                  </div>
                  {expandedTopics.has(`topic-${index}`) ? (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  )}
                </button>
                
                {expandedTopics.has(`topic-${index}`) && (
                  <div className="border-t border-gray-200 p-4 bg-gray-50">
                    <div className="space-y-4">
                      {/* Sample Questions */}
                      <div>
                        <h5 className="font-semibold text-gray-900 mb-3 flex items-center space-x-2">
                          <Lightbulb className="w-4 h-4 text-yellow-600" />
                          <span>練習題目</span>
                        </h5>
                        <div className="space-y-3">
                          {(topic.sampleQuestions || []).map((question, qIndex) => {
                            const drillKey = `${topic.title}-${qIndex}`;
                            const hasDrillData = drillData[drillKey];
                            
                            return (
                              <div key={qIndex} className="bg-white p-4 rounded-lg border border-gray-200">
                                <div className="mb-3">
                                  <div className="flex items-center justify-between mb-2">
                                    <span className="inline-block bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded">
                                      題目 {qIndex + 1}
                                    </span>
                                    <button
                                      onClick={() => handleAIDrill(question, topic.title, qIndex)}
                                      className="flex items-center space-x-1 px-3 py-1 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-lg hover:from-purple-600 hover:to-blue-600 transition-colors text-sm"
                                    >
                                      <Brain className="w-3 h-3" />
                                      <span>AI Drill</span>
                                    </button>
                                  </div>
                                  <p className="text-gray-900">{question.question}</p>
                                </div>
                                
                                <div className="bg-green-50 p-3 rounded-lg">
                                  <h6 className="font-semibold text-green-900 mb-2">答案與解釋：</h6>
                                  <p className="text-green-800 text-sm">{question.answer}</p>
                                  {question.explanation && (
                                    <div className="mt-2 pt-2 border-t border-green-200">
                                      <p className="text-green-700 text-sm">{question.explanation}</p>
                                    </div>
                                  )}
                                </div>

                                {/* AI Drill Questions */}
                                {hasDrillData && (
                                  <div className="mt-4">
                                    <DrillQuestions 
                                      drillData={hasDrillData} 
                                      onNewDrill={handleNewDrill}
                                    />
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* AI Drill Modal */}
      <AIDrillModal
        isOpen={isDrillModalOpen}
        onClose={() => setIsDrillModalOpen(false)}
        originalQuestion={currentDrillQuestion?.question}
        questionType={currentDrillQuestion?.topicTitle}
        grade={grade}
        onDrillGenerated={handleDrillGenerated}
      />
    </div>
  );
};

export default PrimaryGradeContent; 